import React from 'react';
import Icon from '../../../components/AppIcon';

const FeedbackInsightsPanel = ({ feedbackData }) => {
  const generateInsights = () => {
    if (!feedbackData || feedbackData?.length === 0) return [];

    const insights = [];

    // Satisfaction insight
    const avgRating = feedbackData?.reduce((sum, f) => sum + (f?.rating || 0), 0) / feedbackData?.length;
    if (avgRating >= 4.5) {
      insights?.push({
        type: 'success',
        icon: 'TrendingUp',
        title: 'Excellent Satisfaction',
        description: `Average rating of ${avgRating?.toFixed(1)}/5.0 indicates high member satisfaction`,
        color: 'var(--color-success)'
      });
    } else if (avgRating < 3.5) {
      insights?.push({
        type: 'warning',
        icon: 'AlertTriangle',
        title: 'Satisfaction Needs Attention',
        description: `Average rating of ${avgRating?.toFixed(1)}/5.0 suggests improvement opportunities`,
        color: 'var(--color-warning)'
      });
    }

    // Difficulty insight
    const difficultyCount = { too_easy: 0, just_right: 0, too_hard: 0 };
    feedbackData?.forEach(f => {
      if (f?.difficulty_level) difficultyCount[f?.difficulty_level]++;
    });
    
    if (difficultyCount?.too_hard > difficultyCount?.just_right) {
      insights?.push({
        type: 'info',
        icon: 'Activity',
        title: 'Workout Intensity High',
        description: 'Consider offering modified versions or scaling options for workouts',
        color: 'var(--color-primary)'
      });
    } else if (difficultyCount?.too_easy > difficultyCount?.just_right) {
      insights?.push({
        type: 'info',
        icon: 'TrendingUp',
        title: 'Members Ready for Challenge',
        description: 'Members may benefit from more advanced workout progressions',
        color: 'var(--color-accent)'
      });
    }

    // Pain points insight
    const withPainPoints = feedbackData?.filter(f => f?.pain_points)?.length;
    if (withPainPoints > feedbackData?.length * 0.3) {
      insights?.push({
        type: 'warning',
        icon: 'AlertCircle',
        title: 'Pain Reports Elevated',
        description: `${withPainPoints} members reported discomfort. Review exercise form and modifications`,
        color: 'var(--color-error)'
      });
    }

    // Energy level insight
    const lowEnergy = feedbackData?.filter(f => f?.energy_level === 'low')?.length;
    if (lowEnergy > feedbackData?.length * 0.4) {
      insights?.push({
        type: 'info',
        icon: 'Battery',
        title: 'Low Energy Patterns',
        description: 'Consider nutrition guidance or workout timing recommendations',
        color: 'var(--color-warning)'
      });
    }

    return insights;
  };

  const insights = generateInsights();

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
        <Icon name="Lightbulb" size={20} className="text-warning" />
        Key Insights & Recommendations
      </h3>
      
      {insights?.length > 0 ? (
        <div className="space-y-4">
          {insights?.map((insight, index) => (
            <div key={index} className="flex items-start gap-3 p-4 rounded-lg" style={{ backgroundColor: `${insight?.color}10` }}>
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${insight?.color}20` }}
              >
                <Icon name={insight?.icon} size={20} style={{ color: insight?.color }} />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-foreground mb-1">{insight?.title}</h4>
                <p className="text-sm text-muted-foreground">{insight?.description}</p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8">
          <Icon name="BarChart3" size={32} className="text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">Insights will appear as feedback is collected</p>
        </div>
      )}
    </div>
  );
};

export default FeedbackInsightsPanel;